/** Connector ids the pre-run gate can prompt for, in prompt order. */
export const CONNECTOR_IDS = ["gmail", "calendar", "attio"];

/** localStorage: user has connected or explicitly skipped this connector at least once. */
const STORAGE_KEY_BY_CONNECTOR = {
  gmail: "starmesh_performer_gate_gmail_choice_made",
  calendar: "starmesh_performer_gate_calendar_choice_made",
  attio: "starmesh_performer_gate_attio_choice_made",
};

/** Maps a classified action item's `ai_assist_type` to the connector(s) its run needs. */
const ASSIST_TYPE_TO_CONNECTOR = {
  email: "gmail",
  calendar: "calendar",
  crm: "attio",
};

/**
 * Which connector(s) a run for this action item's assist type should be gated on.
 *
 * An unrecognized or missing type checks all three defensively rather than
 * skipping the gate outright.
 *
 * @example
 * resolveRelevantConnectors("email");
 * // ["gmail"]
 *
 * @example
 * resolveRelevantConnectors("");
 * // ["gmail", "calendar", "attio"]
 */
export const resolveRelevantConnectors = (aiAssistType) => {
  const key = String(aiAssistType || "").trim().toLowerCase();
  const single = ASSIST_TYPE_TO_CONNECTOR[key];
  return single ? [single] : [...CONNECTOR_IDS];
};

/**
 * True once the user has connected or explicitly skipped this connector before.
 *
 * @example
 * hasStoredGateChoice("gmail"); // false on first-ever run
 */
export const hasStoredGateChoice = (connectorId) => {
  try {
    return localStorage.getItem(STORAGE_KEY_BY_CONNECTOR[connectorId] || "") === "1";
  } catch {
    return false;
  }
};

/**
 * Persist that the user made a connect-or-skip choice for this connector
 * (the gate will not prompt for it again).
 *
 * @example
 * markGateChoiceMade("gmail");
 * hasStoredGateChoice("gmail"); // true
 */
export const markGateChoiceMade = (connectorId) => {
  const key = STORAGE_KEY_BY_CONNECTOR[connectorId];
  if (!key) return;
  try {
    localStorage.setItem(key, "1");
  } catch {
    // ignore quota / private mode
  }
};

/**
 * Clear a connector's stored gate choice, e.g. after the user disconnects it,
 * so the next run prompts again instead of staying silently skipped.
 *
 * @example
 * clearGateChoice("gmail");
 * hasStoredGateChoice("gmail"); // false
 */
export const clearGateChoice = (connectorId) => {
  const key = STORAGE_KEY_BY_CONNECTOR[connectorId];
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};
